import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

const AuthContext = createContext();

const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const isVerifyingRef = useRef(false);
  const verifyAttemptRef = useRef(0);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (token) {
      if (isVerifyingRef.current) return;
      
      const currentAttempt = ++verifyAttemptRef.current;
      abortControllerRef.current = new AbortController();
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken(abortControllerRef.current.signal, currentAttempt);
    } else {
      setLoading(false);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async (signal, attemptId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal
      });
      
      if (attemptId !== verifyAttemptRef.current) return;

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        const decoded = decodeToken(token);
        const role = decoded?.role === 'super_admin' ? 'admin' : (decoded?.role || 'admin');
        setUserRole(role);
        setError(null);
      } else if (response.status === 401 || response.status === 403) {
        console.warn('Token invalid or expired');
        logout();
      } else {
        setError(`Gagal memverifikasi sesi (Status: ${response.status})`);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && attemptId === verifyAttemptRef.current) {
        console.error('Token verification error:', error);
        setError('Gagal terhubung ke server');
      }
    } finally {
      if (attemptId === verifyAttemptRef.current) {
        setLoading(false);
      }
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔐 Attempting login with:', { username: credentials.username });
      
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Failed to parse login response:', e);
        data = { error: `Server error: ${response.status} ${response.statusText}` };
      }

      console.log('📥 Login response:', { ok: response.ok, data });

      if (!response.ok) {
        const errorMessage = data.error || `Login gagal (${response.status})`;
        console.error('❌ Login failed:', errorMessage);
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      if (!data.token) {
        console.error('❌ No token in response');
        setError('Token tidak diterima dari server');
        return { success: false, error: 'Token tidak diterima dari server' };
      }

      localStorage.setItem('token', data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      
      const decodedToken = decodeToken(data.token);
      const role = decodedToken?.role === 'super_admin' ? 'admin' : (decodedToken?.role || 'admin');
      
      console.log('✅ Login successful, token stored, triggering verification...');
      setLoading(false);
      
      setToken(data.token);
      
      return { success: true, role };
    } catch (error) {
      console.error('❌ Login error:', error);
      const message = error.message || 'Login gagal. Periksa koneksi internet Anda.';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    isVerifyingRef.current = false;
    verifyAttemptRef.current += 1;
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUserRole(null);
    setError(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Gagal mengubah password';
      return { success: false, error: message };
    }
  };

  const updateUser = (newUserData) => {
    setUser(newUserData);
  };

  const getUserRole = () => userRole;

  const value = {
    user,
    userRole,
    getUserRole,
    loading,
    error,
    login,
    logout,
    changePassword,
    updateUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
